// Profile — the anonymous demo session's home. No login language, no
// account creation. Just the visitor's demo identity + session controls.

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, RefreshCcw, Sparkles, Pencil, Check, Library } from "lucide-react";
import { useCommons } from "../lib/store";
import { useTour } from "../tour/TourProvider";
import { Avatar } from "../components/Avatar";
import { updateMe } from "../lib/api";
import ProjectCardCompact from "../components/ProjectCardCompact";
import { PastAttemptStack } from "../components/PastAttemptStack";
import { youStewarding, youJoined } from "../lib/domain";

export default function Profile() {
  const nav = useNavigate();
  const { data, user, setUser, refresh, resetAll } = useCommons();
  const tour = useTour();
  const [resetOpen, setResetOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [nameDraft, setNameDraft] = useState("");

  if (!data || !user) return <div className="c-load">Loading…</div>;

  const isDefaultName = user.name === "You";

  const openEdit = () => {
    setNameDraft(isDefaultName ? "" : user.name);
    setEditing(true);
  };
  const saveName = async () => {
    const clean = nameDraft.trim();
    if (clean.length < 2) { setEditing(false); return; }
    await updateMe(user.personId, { name: clean });
    setUser({ ...user, name: clean });
    await refresh();
    setEditing(false);
  };

  const stewarding = youStewarding(user.personId, data.projects);
  const joined = youJoined(user.personId, data.projects, data.pledges);
  // Project Library doorway visibility. The doorway itself is shown
  // unconditionally so a curious visitor can discover it, but the
  // subcopy hints when the back-shelf collection has entries.
  const overflowCount = data.projects.filter(
    (p) => p.libraryPlacement === "overflow"
  ).length;

  const onResetConfirm = async () => {
    await resetAll();
    setResetOpen(false);
    setEditing(false);
    nav("/", { replace: true });
  };

  return (
    <div className="c-page" data-testid="profile-page">
      <div className="c-page-topbar">
        <button
          className="c-back-row"
          onClick={() => nav("/")}
          data-testid="profile-back-btn"
          aria-label="Back to Home"
        >
          <ChevronLeft size={18} /> Commons
        </button>
      </div>

      <div className="c-profile-hero" data-testid="profile-hero">
        <Avatar name={user.name} me size="lg" as="div" className="c-avatar-hero" />
        <div style={{ flex: 1, minWidth: 0 }}>
          {editing ? (
            <div className="c-profile-edit-row">
              <input
                className="c-input"
                autoFocus
                value={nameDraft}
                onChange={(e) => setNameDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") saveName(); if (e.key === "Escape") setEditing(false); }}
                placeholder="Your first name"
                aria-label="Your first name"
                data-testid="profile-name-input"
                style={{ maxWidth: 220 }}
              />
              <button
                className="c-icon-btn"
                onClick={saveName}
                aria-label="Save name"
                data-testid="profile-name-save"
              >
                <Check size={18} />
              </button>
            </div>
          ) : (
            <div className="c-profile-name-row">
              <h1 className="c-h1 c-profile-hero-title">
                {user.name}
                <span className="c-profile-hero-sparkle" aria-hidden="true">✨</span>
              </h1>
              <button
                className="c-icon-btn"
                onClick={openEdit}
                aria-label="Edit name"
                data-testid="profile-name-edit"
              >
                <Pencil size={16} />
              </button>
            </div>
          )}
          {(() => {
            // Compact orientation to the person's Commons activity. Uses
            // the same `stewarding` and `joined` arrays that drive the
            // page's project sections — the numbers cannot drift.
            const s = stewarding.length;
            const j = joined.length;
            if (s === 0 && j === 0) return null;
            const parts = [];
            if (s > 0) parts.push(`${s} stewarding`);
            if (j > 0) parts.push(`${j} joined`);
            return (
              <div
                className="c-participation-summary"
                data-testid="profile-participation-summary"
              >
                {parts.join(" · ")}
              </div>
            );
          })()}
          <div className="c-helper">
            {isDefaultName
              ? "You're exploring Commons as a guest. Add a first name if you'd like — it stays inside this private demo session."
              : "Your anonymous demo session. Nothing here is saved to an account — it's just a private sandbox you can explore."}
          </div>
        </div>
      </div>

      {/* Section-jump table of contents. Table-of-contents, not
       * navigation bar: a subtle inline row that helps the visitor
       * move around this page now that participation history can
       * exceed 20 project cards. Only rendered when there are 2+
       * destinations — a single-destination TOC would read oddly.
       * Adding a new You section = give its wrapper an id and add
       * one entry to `tocItems` below; no other change needed.
       */}
      {(() => {
        const tocItems = [
          stewarding.length > 0 && { label: "Stewarding", targetId: "you-stewarding" },
          joined.length > 0 && { label: "Joined", targetId: "you-joined" },
          { label: "Demo tools", targetId: "you-about" },
        ].filter(Boolean);
        if (tocItems.length < 2) return null;
        const jumpTo = (id) => {
          const el = document.getElementById(id);
          if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        };
        return (
          <nav
            className="c-profile-toc"
            aria-label="Sections on this page"
            data-testid="profile-toc"
          >
            {tocItems.map((it, i) => (
              <span key={it.targetId} className="c-profile-toc-item">
                {i > 0 ? <span className="c-profile-toc-sep" aria-hidden="true">·</span> : null}
                <button
                  type="button"
                  className="c-profile-toc-link"
                  onClick={() => jumpTo(it.targetId)}
                  data-testid={`profile-toc-${it.targetId}`}
                >
                  {it.label}
                </button>
              </span>
            ))}
          </nav>
        );
      })()}

      {stewarding.length ? (
        <div
          id="you-stewarding"
          className="c-section"
          data-testid="profile-section-stewarding"
          data-tour="profile-participation"
        >
          <div className="c-section-title">Projects you're stewarding</div>
          {/* Contextual creation invitation, placed beneath the heading
              so it reads as a natural continuation of "here's what I'm
              stewarding — including the possibility of starting one
              more". Same quiet editorial voice as Home's "Start with an
              idea →" and the library outro's "Start a project →". */}
          <div className="c-steward-outro" data-tour="profile-start-project">
            <button
              type="button"
              className="c-steward-outro-doorway"
              onClick={() => nav("/create")}
              data-testid="profile-steward-outro-start"
            >
              Start a project <span aria-hidden="true">→</span>
            </button>
          </div>
          {stewarding.map((p) => (
            <div key={p.id} className="c-project-with-history">
              <ProjectCardCompact
                project={p}
                pledges={data.pledges}
                people={data.people}
                context="profile"
              />
              <PastAttemptStack
                project={p}
                allProjects={data.projects}
                pledges={data.pledges}
              />
            </div>
          ))}
        </div>
      ) : null}

      {joined.length ? (
        <div
          id="you-joined"
          className="c-section"
          data-testid="profile-section-joined"
          data-tour="profile-joined"
        >
          <div className="c-section-title">Projects you've joined</div>
          {joined.map((p) => (
            <div key={p.id} className="c-project-with-history">
              <ProjectCardCompact
                project={p}
                pledges={data.pledges}
                people={data.people}
                context="profile"
              />
              <PastAttemptStack
                project={p}
                allProjects={data.projects}
                pledges={data.pledges}
              />
            </div>
          ))}
        </div>
      ) : null}

      <div id="you-about" className="c-section" data-testid="profile-controls">
        <div className="c-section-title">Demo tools</div>
        <button
          className="c-settings-row"
          onClick={() => nav("/library")}
          data-testid="profile-project-library-btn"
        >
          <Library size={18} color="var(--c-on-surface)" />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700 }}>Project Library</div>
            <div className="c-helper" style={{ marginTop: 2 }}>
              {overflowCount > 0
                ? "More projects in Commons."
                : "Browse Commons' wider collection."}
            </div>
          </div>
          <ChevronRight size={16} color="var(--c-muted)" />
        </button>
        <button
          className="c-settings-row"
          onClick={() => { tour.restartInPlace(); nav("/"); }}
          data-testid="profile-restart-tour-btn"
        >
          <Sparkles size={18} color="var(--c-on-surface)" />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700 }}>Restart guided tour</div>
            <div className="c-helper" style={{ marginTop: 2 }}>
              Take the Commons tour again.
            </div>
          </div>
          <ChevronRight size={16} color="var(--c-muted)" />
        </button>
        <button
          className="c-settings-row"
          onClick={() => setResetOpen(true)}
          data-testid="profile-reset-btn"
        >
          <RefreshCcw size={18} color="var(--c-on-surface)" />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700 }}>Start fresh</div>
            <div className="c-helper" style={{ marginTop: 2 }}>
              Clear your demo activity and begin again.
            </div>
          </div>
          <ChevronRight size={16} color="var(--c-muted)" />
        </button>
      </div>

      {resetOpen && (
        <div className="c-modal-scrim">
          <div className="c-modal" data-testid="reset-confirm-card">
            <div className="c-h2">Start a fresh demo?</div>
            <div className="c-body">
              This will clear the commitments and changes you've made during this demo and return Commons to its starting state.
            </div>
            <div className="c-modal-row">
              <button className="c-btn c-btn-ghost" onClick={() => setResetOpen(false)} data-testid="reset-cancel-btn">Cancel</button>
              <button className="c-btn c-btn-primary" onClick={onResetConfirm} data-testid="reset-confirm-btn">Start fresh</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
